/**
 * Tiny analytics SDK — fires events to /api/analytics/event.
 *
 * Why server-side:
 *   - No Google Analytics / FB pixel / Mixpanel
 *   - One round-trip, fire-and-forget
 *   - Privacy posture stays clean
 *
 * Usage:
 *   import { track } from "@/lib/analytics";
 *   track("landing_view");
 *   track("checkout_start", { plan: "individual_yearly" });
 *
 * Events allowed (must match backend ALLOWED_EVENTS whitelist):
 *   landing_view, register_start, register_complete, login_success,
 *   first_tile_added, day_1_return, checkout_start, checkout_complete,
 *   tile_template_applied
 */
import { api } from "@/lib/api";

const SESSION_KEY = "sonspeak_session_id";

function sessionId() {
  try {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
      id = (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

const inflight = new Set();

export function track(name, metadata = undefined) {
  if (!name) return;
  // Dedupe within a single session for events that should only fire once
  // per page-load (landing_view, register_complete, etc.)
  const onceKey = `once:${name}`;
  if (ONCE_PER_SESSION.has(name)) {
    if (inflight.has(onceKey)) return;
    inflight.add(onceKey);
  }
  api.post("/analytics/event", {
    name,
    session_id: sessionId(),
    metadata: metadata || undefined,
  }).catch(() => { /* analytics never throws to caller */ });
}

const ONCE_PER_SESSION = new Set([
  "landing_view",
  "register_start",
  "register_complete",
  "day_1_return",
]);
